// Enhanced UploadSheetFile.tsx - Following CSV patterns exactly while preserving ALL existing functionality
import React, { useState, useEffect, ChangeEvent, useCallback, useRef } from "react";
import axios from 'axios';
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import styled from 'styled-components';
import {
  FaFileUpload,
  FaGoogle,
  FaCheckCircle,
  FaSpinner,
  FaGoogleDrive,
  FaFileAlt,
  FaSignOutAlt,
  FaInfoCircle,
} from "react-icons/fa";

// PRESERVED COMPONENTS - NO CHANGES
import DynamicConfirmationModal from "../src/components/DynamicConfirmationModal";
import { setLeads } from "../src/actions/actions";
import { downloadCsv, getAllLeads } from "../src/services/leadService";
import {
  getGoogleSheets,
  getGoogleSheetsInfo,
  getGoogleTokens,
  importGoogleSheet,
} from "../src/services/authServices";
import { useGoogleLogin, CodeResponse, TokenResponse } from '@react-oauth/google';
import { GoogleAuthService } from '../src/services/GoogleAuthService';

// NEW IMPORTS - CSV utilities and popup
import PopupComponent from "../src/components/PopupComponent";
import { 
  generateAutoMappings, 
  validateLeadData, 
  LEAD_FIELD_SYNONYMS,
  LeadFieldDefinition 
} from "../src/utils/csvUtilities";
import { Upload, ArrowRightLeft, Shield, Trophy, Check, HelpCircle } from "lucide-react";

// PRESERVED STYLED COMPONENTS - keeping existing ones
import {
  DrawerContent,
  StepIndicatorContainer,
  StepIndicator,
  StepDot,
  StepConnector,
  StepContainer,
  StepTitle,
  StepDescription,
  GoogleButton,
  SheetList,
  SheetItem,
  SheetIcon,
  SheetDetails,
  SheetName,
  SheetDate,
  MappingContainer,
  MappingItem,
  MappingLabel,
  MappingSelect,
  ImportButton,
  SuccessIcon,
  DropdownItem,
  CloseButton,
  StepIndicatorSection,
  DisconnectButton,
} from './UploadSheetFile.styles';
import GenericConfirmationModal from "./components/GenericConfirmationModal";
import logger from "./utils/logger";

// NEW STYLED COMPONENTS - following CSV patterns
const PopupContentContainer = styled.div`
  height: calc(80vh - 120px);
  display: flex;
  overflow: hidden;
`;

const LeftSidebar = styled.div`
  width: 280px;
  border-right: 1px solid #e2e8f0;
  padding: 32px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  display: flex;
  flex-direction: column;
  position: relative;
`;

const RightContent = styled.div`
  flex: 1;
  display: flex;
  flexDirection: column;
  overflow: hidden;
`;

const ErrorDisplay = styled.div`
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 16px 16px 0 16px;
  display: flex;
  align-items: center;
`;

const ContentArea = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 16px 24px;
`;

const ValidationResults = styled.div`
  .validation-summary {
    background: #f6ffed;
    border: 1px solid #b7eb8f;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 24px;
  }

  .field-validation {
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;

    &.has-issues {
      border-color: #ff7875;
      background: #fff2f0;
    }

    &.valid {
      border-color: #52c41a;
      background: #f6ffed;
    }
  }
`;

const CompletionContent = styled.div`
  text-align: center;
  padding: 30px 20px;

  .success-icon {
    font-size: 48px;
    color: #52c41a;
    margin-bottom: 16px;
  }
`;

// PRESERVED INTERFACES - NO CHANGES
interface Props {
  selectedList: {
    id: string;
    name: string;
  };
}

interface AuthError extends Error {
  code: 'TOKEN_INVALID' | 'NETWORK_ERROR' | 'AUTH_REQUIRED' | 'RATE_LIMIT' | 'SERVER_ERROR';
}

interface GoogleSheetsInfoResponse {
  statusCode: number;
  status: boolean;
  data: string[];
}

interface Sheet {
  id: string;
  name: string;
}

interface GoogleTokens {
  access_token: string;
  scope: string;
  token_type: string;
  expiry_date: number;
}

interface RootState {
  rootReducers: {
    user?: {
      userPreferences?: {
        customForm?: Array<{ name: string; value: string; label: string; required?: boolean; isRequired?: boolean; [key: string]: any }>;
      };
    };
  };
}

// PRESERVED COMPONENT - keeping exact same name and structure
const UploadSheetFile: React.FC<Props> = ({ selectedList }) => {
  logger.log('🔍 UploadSheetFile Enhanced - Props:', { selectedList });

  // PRESERVED STATE VARIABLES - keeping all existing ones
  const [currentStep, setCurrentStep] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sheetsList, setSheetsList] = useState<Sheet[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const [sheetHeaders, setSheetHeaders] = useState<string[]>([]);
  const [mappedFields, setMappedFields] = useState<{ [key: string]: string }>({});
  const [formCloseModel, setFormCloseModel] = useState<boolean>(false);
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false);
  const [showingImportDialog, setShowingImportDialog] = useState(false);

  // NEW STATE VARIABLES - for enhanced functionality
  const [leadFields, setLeadFields] = useState<LeadFieldDefinition[]>([]);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<{ total: number; successful: number; failed: number }>({
    total: 0,
    successful: 0,
    failed: 0
  });

  // PRESERVED REDUX INTEGRATION - NO CHANGES
  const dispatch = useDispatch();
  const StoreData = useSelector((state: RootState) => state.rootReducers);

  // PRESERVED GOOGLE LOGIN FUNCTION - 100% EXACT
  const handleGoogleLoginSuccess = async (codeResponse: CodeResponse) => {
    try {
      setIsLoading(true);
      await GoogleAuthService.sendGoogleCode(codeResponse.code);
  
      const { isValid, hasRequiredScopes } = await GoogleAuthService.checkExistingTokens();
      
      if (isValid && hasRequiredScopes) {
        toast.success('Successfully connected to Google Sheets');
        await fetchGoogleSheets();
        setCurrentStep(1);
      } else {
        throw new Error('Failed to obtain required permissions');
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error('Failed to connect to Google Sheets. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // PRESERVED LOGIN HOOK - 100% EXACT
  const login = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: handleGoogleLoginSuccess,
    onError: (error) => {
      console.error('Google Login Error:', error);
      toast.error('Failed to authenticate with Google');
      setIsLoading(false);
    },
    scope: GoogleAuthService.SHEETS_SCOPE
  });

  // PRESERVED FIELD MAPPING FUNCTION - 100% EXACT

const handleFieldMapping = (fieldName: string, sheetHeader: string): void => {
  setMappedFields((prev) => ({
    ...prev,
    [fieldName]: sheetHeader  // ✅ fieldName is field.name - CORRECT!
  }));
};

  // PRESERVED TOKEN CHECK EFFECT - 100% EXACT
  useEffect(() => {
    const checkTokenAndProceed = async () => {
      if (currentStep === 0) {
        try {
          setIsLoading(true);
          const { isValid, hasRequiredScopes } = await GoogleAuthService.checkExistingTokens();
          
          if (isValid && hasRequiredScopes) {
            await fetchGoogleSheets();
            setCurrentStep(1);
          }
        } catch (error) {
          console.error('Token check failed:', error);
          toast.error('Failed to verify Google authentication status');
        } finally {
          setIsLoading(false);
        }
      }
    };
  
    checkTokenAndProceed();
  }, [currentStep]);


  // Add this function after your existing functions in UploadSheetFile.tsx
const downloadTemplate = useCallback(async () => {
  logger.log('📥 Downloading CSV template from server...');
  
  try {
    const response = await downloadCsv("");
    
    if (response && response.status) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "google-sheets-template.csv");
      document.body.appendChild(link);
      link.click();
      toast.success("Template downloaded successfully");
      logger.log('✅ Template downloaded successfully');
    }
  } catch (err) {
    console.error('❌ Error downloading template:', err);
    toast.error("Error while downloading CSV template!");
  }
}, []);

  // PRESERVED ERROR HANDLER - 100% EXACT
  const handleSheetError = (error: { response: { data: { message: any; }; }; }) => {
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      const errorMessage = error.response.data.message;
      
      if (errorMessage.includes("Unable to parse range") && 
          errorMessage.includes("Sheet1!A1:Z1")) {
        
        return toast.error(
          "We can only read from the first tab in your spreadsheet, and it must be named 'Sheet1'. " +
          "Please rename your first sheet tab to 'Sheet1' and try again.",
          { duration: 6000 }
        );
      }
    }
    
    toast.error("Failed to import sheet. Please check your sheet structure and try again.");
  }

  // ENHANCED SHEET SELECT FUNCTION - adding auto-mapping
  const handleSheetSelect = async (sheetId: string): Promise<void> => {
    try {
      setIsLoading(true);
      setSelectedSheet(sheetId);
      
      const response = await getGoogleSheetsInfo(sheetId);
      const responseData = response.data as GoogleSheetsInfoResponse;
      
      if (responseData.statusCode === 200 && responseData.status && Array.isArray(responseData.data)) {
        const cleanedHeaders = responseData.data
          .map(header => header.trim())
          .filter(header => header.length > 0);
        
        setSheetHeaders(cleanedHeaders);
        
        // ADD AUTO-MAPPING - NEW ENHANCEMENT
        if (leadFields.length > 0 && cleanedHeaders.length > 0) {
          logger.log('🤖 Generating auto-mappings for Google Sheets');
          const autoMappings = generateAutoMappings(cleanedHeaders, leadFields);
          
          setMappedFields(prev => ({
            ...prev,
            ...autoMappings
          }));
          
          if (Object.keys(autoMappings).length > 0) {
            toast.success(`Auto-mapped ${Object.keys(autoMappings).length} fields`);
          }
        }
        
        setCurrentStep(2);
      } else {
        throw new Error('Invalid header data received');
      }
    } catch (error) {
      console.error('Header fetch error:', error);
      toast.error('Failed to fetch sheet headers');
      setSheetHeaders([]);
    } finally {
      setIsLoading(false);
    }
  };
  
// REPLACE with enhanced version that includes logging
const getFilteredMappings = () => {
  logger.log('🔍 Getting filtered mappings from:', mappedFields);
  
  const filtered = Object.entries(mappedFields)
    .filter(([_, value]) => value !== "" && value !== "Don't import")
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {} as { [key: string]: string });
  
  logger.log('✅ Filtered mappings (field.name format):', filtered);
  return filtered;
};

  // ADD THIS NEW FUNCTION - Convert field.name mappings to field.value for API
const convertMappingsToApiFormat = (mappings: Record<string, string>): Record<string, string> => {
  logger.log('🔄 Converting mappings from field.name to field.value for API');
  
  // Get the conversion mapping from Redux store
  const form_Data = StoreData.user?.userPreferences?.customForm || [];
  const keyMapping = form_Data.reduce((acc: any, obj: any) => {
    acc[obj.name] = obj.value; // Maps field.name -> field.value
    return acc;
  }, {});
  
  logger.log('🗺️ Key mapping (name->value):', keyMapping);
  
  // Convert the mappings
  const convertedMappings = Object.keys(mappings).reduce((acc: any, fieldName) => {
    const csvColumn = mappings[fieldName];
    if (csvColumn && csvColumn !== "Don't import" && csvColumn !== "") {
      const apiKey = keyMapping[fieldName] || fieldName; // Convert to field.value
      acc[apiKey] = csvColumn;
      logger.log(`🔄 Converted: ${fieldName} -> ${apiKey} = ${csvColumn}`);
    }
    return acc;
  }, {});
  
  logger.log('✅ Final API mappings:', convertedMappings);
  return convertedMappings;
};

  // PRESERVED FETCH GOOGLE SHEETS - 100% EXACT
  const fetchGoogleSheets = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await getGoogleSheets();
      
      if (response?.data?.data) {
        setSheetsList(response.data.data);
      } else {
        throw new Error('No sheets data available');
      }
    } catch (error: unknown) {
      console.error('Fetch sheets error:', error);
      
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        toast.error('Session expired. Please reconnect to Google Sheets');
        setCurrentStep(0);
      } else {
        toast.error('Failed to fetch Google Sheets');
        setSheetsList([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // PRESERVED GOOGLE AUTH FUNCTION - 100% EXACT
  const handleGoogleAuth = async (): Promise<void> => {
    try {
      setIsLoading(true);
      login();
    } catch (error) {
      console.error('Google auth error:', error);
      toast.error('Failed to authenticate with Google');
    } finally {
      setIsLoading(false);
    }
  };

  // PRESERVED DISCONNECT FUNCTION - 100% EXACT
  const handleDisconnect = async () => {
    try {
      setIsDisconnecting(true);
      await GoogleAuthService.disconnect();
      toast.success('Successfully disconnected from Google');
      setCurrentStep(-1);
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error('Failed to disconnect from Google');
    } finally {
      setIsDisconnecting(false);
    }
  };

  // ENHANCED IMPORT LEADS - adding validation check
const importLeads = () => {
  if (!selectedSheet) {
    toast.error("Please select a sheet first");
    return;
  }
  
  const filteredMappings = getFilteredMappings();
  if (Object.keys(filteredMappings).length === 0) {
    toast.error("Please map at least one field");
    return;
  }
  
  // Go directly to import dialog
  setShowingImportDialog(true);
}

  // NEW VALIDATION FUNCTION
const validateAndProceed = () => {
  logger.log('🎯 validateAndProceed called - showing import dialog');
  
  if (!selectedSheet || sheetHeaders.length === 0) {
    toast.error("No sheet data available for validation");
    return;
  }

  try {
    setIsLoading(true);
    setError(null);
    
    // Create mock data for validation (since we can't fetch actual sheet data easily)
    const mockData = sheetHeaders.map((header, index) => {
      const sampleData: any = {};
      sampleData[header] = `Sample ${header} ${index + 1}`;
      return sampleData;
    });

    // Get current mappings for validation
    const filteredMappings = getFilteredMappings();
    
    // Validate the mappings using the enhanced validation from CSV utilities
    const validation = validateLeadData(mockData, filteredMappings, leadFields);
    setValidationResult(validation);
    
    logger.log('🎯 Validation completed:', validation);
    
    toast.success('Validation completed - ready to import');
    
    // ✅ FIXED: Show import dialog ONLY after validation is complete
    setShowingImportDialog(true);
    
  } catch (error) {
    console.error('❌ Validation error:', error);
    setError('Failed to validate data. Please try again.');
    setValidationResult(null);
    setShowingImportDialog(false); // Don't show dialog if validation fails
  } finally {
    setIsLoading(false);
  }
};
  
const handleRegularImport = async () => {
  try {
    setIsLoading(true);
    
    const filteredMappings = getFilteredMappings();
    const apiMappings = convertMappingsToApiFormat(filteredMappings);

    const response = await importGoogleSheet({
      spreadsheetId: selectedSheet,
      mapping: apiMappings,
      list: selectedList?.id && selectedList.id !== "0" ? selectedList.id : undefined,
    });

    logger.log('🔍 DEBUG - selectedList prop:', selectedList);

    const leadsResponse = await getAllLeads({
      isAscending: false,
      page: 1,
      perPage: 10,
    });

    if (leadsResponse && leadsResponse.status) {
      dispatch(setLeads(leadsResponse.data.data));
    }

    toast.success("Leads imported successfully");
    setCurrentStep(3); // Move to success step
    setShowingImportDialog(false); // ✅ Only close on SUCCESS
    
  } catch (error) {
    console.error("Error importing leads:", error);
    toast.error("Failed to import leads. Please try again."); // ✅ Just show toast
    // ❌ DON'T close dialog - let user try again
  } finally {
    setIsLoading(false);
    // ❌ DON'T close dialog here - moved to success block only
  }
};

  // PRESERVED SYNC IMPORT - 100% EXACT
 // COMPLETE handleSyncImport replacement - with proper error handling

const handleSyncImport = async () => {
  try {
    setIsLoading(true);
    logger.log('🔄 Starting sync import process');
    
    // Get the mappings in the correct format for API
    const filteredMappings = getFilteredMappings();
    const apiMappings = convertMappingsToApiFormat(filteredMappings);
    
    logger.log('🔄 Setting up sync integration with mappings:', apiMappings);
    
    // Step 1: Set up the sync integration
    const syncResponse = await fetch('https://mapi2.3sigmacrm.com/api/v1/integration/user-integration', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem("auth_token") || "",
      },
      body: JSON.stringify({
        integrationKey: "google_spread_sheets",
        details: [{
          spreadsheetId: selectedSheet,
          spreadsheetName: sheetsList.find(s => s.id === selectedSheet)?.name || 'Unknown Sheet',
          range: "A1:Z10000",
          isFirstTimeCompleted: false,
          isRecursive: true,
          mapping: apiMappings,  // ✅ Using field.value keys for API
          list: selectedList?.id && selectedList.id !== "0" ? selectedList.id : undefined
        }]
      })
    });

    if (!syncResponse.ok) {
      throw new Error(`Sync setup failed: ${syncResponse.status} ${syncResponse.statusText}`);
    }

    logger.log('✅ Sync integration setup successful');

    // Step 2: Do the initial import using the same logic as regular import
    logger.log('🔄 Performing initial import after sync setup');
    
    const importResponse = await importGoogleSheet({
      spreadsheetId: selectedSheet,
      mapping: apiMappings,  // ✅ Using field.value keys for API
      list: selectedList?.id && selectedList.id !== "0" ? selectedList.id : undefined,
    });

    logger.log('✅ Initial import successful:', importResponse);

    // Step 3: Refresh the leads data in Redux store
    logger.log('🔄 Refreshing leads data');
    
    const leadsResponse = await getAllLeads({
      isAscending: false,
      page: 1,
      perPage: 10,
    });

    if (leadsResponse && leadsResponse.status) {
      dispatch(setLeads(leadsResponse.data.data));
      logger.log('✅ Leads data refreshed in store');
    }

    // Step 4: Success - close dialog and move to success step
    toast.success("Import completed and sync setup successfully! 🎉");
    setCurrentStep(3); // Move to success step
    setShowingImportDialog(false); // ✅ Only close on SUCCESS
    
  } catch (error) {
    console.error("❌ Error setting up sync import:", error);
    
    // Show user-friendly error message based on error type
    if (error instanceof Error) {
      if (error.message.includes('401') || error.message.includes('unauthorized')) {
        toast.error("Authentication failed. Please reconnect to Google Sheets.");
      } else if (error.message.includes('400')) {
        toast.error("Invalid data format. Please check your field mappings and try again.");
      } else if (error.message.includes('403')) {
        toast.error("Permission denied. Please check your Google Sheets access permissions.");
      } else if (error.message.includes('404')) {
        toast.error("Sheet not found. Please ensure the sheet still exists and try again.");
      } else if (error.message.includes('429')) {
        toast.error("Too many requests. Please wait a moment and try again.");
      } else {
        toast.error("Failed to setup sync. Please try again.");
      }
    } else {
      toast.error("Failed to setup sync. Please try again.");
    }
    
    // ❌ DON'T close dialog on error - let user try again
    
  } finally {
    setIsLoading(false);
    logger.log('🏁 Sync import process completed');
    // ❌ DON'T close dialog here - only close on success
  }
};

  // PRESERVED INITIAL MAPPING SETUP - 100% EXACT
  useEffect(() => {
    if (StoreData.user?.userPreferences?.customForm) {
      const customFields: LeadFieldDefinition[] = StoreData.user.userPreferences.customForm.map(field => ({
        name: field.name,
        label: field.label || field.name,
        value: field.value,
        required: field.required || field.isRequired || false,
        isRequired: field.required || field.isRequired || false
      }));
      
      setLeadFields(customFields);
      
    const initialMapping = customFields.reduce<{ [key: string]: string }>(
  (acc, field) => {
    acc[field.name] = ""; // ✅ Use field.name for consistency
    return acc;
  },
  {}
);
      setMappedFields(initialMapping);
    }
  }, [StoreData]);

  // NEW IMPORT DIALOG COMPONENT - following CSV pattern
  const ImportDialog: React.FC<{
    onRegularImport: () => void;
    onSyncImport: () => void;
    onCancel: () => void;
    isLoading: boolean;
  }> = ({ onRegularImport, onSyncImport, onCancel, isLoading }) => (
    <StepContainer>
      <StepTitle>Enable Auto-Sync?</StepTitle>
      <StepDescription>
        <div style={{ marginBottom: '16px' }}>
          <strong>One-time Import:</strong> Import all leads from the sheet once.
        </div>
        <div style={{ marginBottom: '16px' }}>
          <strong>Keep Synced:</strong> Automatically import new leads every few minutes. Perfect if:
          <ul style={{ textAlign: 'left', marginTop: '8px', marginBottom: '8px' }}>
            • Your sheet is connected to Google Forms<br/>
            • You frequently add new leads to this sheet<br/>
            • You have a team updating this sheet
          </ul>
        </div>
      </StepDescription>
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'center' }}>
        <ImportButton 
          onClick={onSyncImport}
          disabled={isLoading}
          style={{ backgroundColor: '#10B981' }}
        >
          {isLoading ? <FaSpinner className="spinner" /> : "Yes, Keep Synced"}
        </ImportButton>
        <ImportButton
          onClick={onRegularImport}
          disabled={isLoading}
        >
          Just Import Once
        </ImportButton>
      </div>
    </StepContainer>
  );

  // ENHANCED RENDER STEP - adding validation step
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepContainer>
            <StepTitle>Connect to Google Sheets</StepTitle>
            <StepDescription>
              Streamline your lead import process by connecting directly to your
              Google Sheets. This secure connection allows you to easily select
              and import lead data.
            </StepDescription>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <GoogleButton onClick={handleGoogleAuth} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <FaSpinner className="spinner" />
                    {currentStep === 0 ? "Checking connection..." : "Connecting..."}
                  </>
                ) : (
                  <>
                    <FaGoogle />
                    Connect Google Sheets
                  </>
                )}
              </GoogleButton>
            </div>
          </StepContainer>
        );
  
      case 1:
        return (
          <StepContainer>
            <StepTitle>Select Google Sheet</StepTitle>
            <StepDescription>
              Choose the Google Sheet containing your lead data. Only sheets you
              have access to will be displayed.
            </StepDescription>

            <SheetList>
              {Array.isArray(sheetsList) && sheetsList.length > 0 ? (
                sheetsList.map((sheet) => (
                  <SheetItem
                    key={sheet.id}
                    onClick={() => handleSheetSelect(sheet.id)}
                  >
                    <SheetIcon>
                      <FaFileAlt />
                    </SheetIcon>
                    <SheetName>{sheet.name}</SheetName>
                  </SheetItem>
                ))
              ) : (
                <StepDescription>No sheets available</StepDescription>
              )}
            </SheetList>
            
              <div style={{
        background: '#f0f9ff',
        borderRadius: '8px',
        padding: '16px',
        margin: '16px 0',
        borderLeft: '4px solid #3b82f6',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start'
      }}>
        <FaInfoCircle size={18} style={{ color: '#3b82f6', marginTop: '2px', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <strong>Need a template?</strong>
          <p style={{ margin: '8px 0', fontSize: '14px', color: '#666' }}>
            Download our CSV template to see the expected format for your Google Sheet.
          </p>
          <button 
            onClick={downloadTemplate}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaFileUpload size={14} />
            Download CSV Template
          </button>
        </div>
      </div>
      
            <div style={{
              background: '#f0f9ff',
              borderRadius: '8px',
              padding: '16px',
              margin: '16px 0',
              borderLeft: '4px solid #3b82f6',
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start'
            }}>
              <FaInfoCircle size={18} style={{ color: '#3b82f6', marginTop: '2px', flexShrink: 0 }} />
              <div>
                <strong>Sheet Requirements:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>First tab must be named <code>Sheet1</code></li>
                  <li>First row should contain headers</li>
                </ul>
                <a 
                  href="https://help.3sigmacrm.com/welcome/adding-or-importing-leads/import-from-google-sheets" 
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#3b82f6', textDecoration: 'underline', fontWeight: '500', marginTop: '8px', display: 'block' }}
                >
                  View our step-by-step import guide
                </a>
              </div>
            </div>
          </StepContainer>
        );
      
   case 2:
  logger.log('🔍 Case 2 - showingImportDialog:', showingImportDialog);
  
  // If showing import dialog, show it OVER the mapping interface
  if (showingImportDialog) {
    logger.log('🎯 Rendering ImportDialog in case 2');
    return (
      <ImportDialog
        onRegularImport={handleRegularImport}
        onSyncImport={handleSyncImport}
        onCancel={() => setShowingImportDialog(false)}
        isLoading={isLoading}
      />
    );
  }

  logger.log('🗺️ Rendering mapping interface in case 2');
  // Always show the mapping interface first
  return (
    <StepContainer>
      <StepTitle>Map Fields</StepTitle>
      <StepDescription>
        Match your Google Sheet columns to the corresponding fields in
        your CRM. Auto-mapped fields are already selected for you.
      </StepDescription>
      <MappingContainer>
        {leadFields.map((field, index) => (
          <MappingItem key={field.name}>
            <MappingLabel>
              {field.label || field.name}
              {(field.required || field.isRequired) && <span style={{ color: '#ff4d4f' }}> *</span>}
            </MappingLabel>
            <MappingSelect
              value={mappedFields[field.name] || ""}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                handleFieldMapping(field.name, e.target.value)
              }
            >
              <option value="">Select column</option>
              {sheetHeaders.length > 0 ? (
                sheetHeaders.map((header) => {
                  const isAlreadyUsed = Object.entries(mappedFields).some(
                    ([otherFieldName, selectedHeader]) => 
                      otherFieldName !== field.name && 
                      selectedHeader === header && 
                      header !== "Don't import"
                  );
                  
                  return (
                    <option 
                      key={header} 
                      value={header}
                      disabled={isAlreadyUsed}
                      style={isAlreadyUsed ? { color: '#ccc' } : {}}
                    >
                      {header} {isAlreadyUsed ? '(already used)' : ''}
                    </option>
                  );
                })
              ) : (
                <option value="" disabled>No headers available</option>
              )}
              <option value="Don't import">Don't import</option>
            </MappingSelect>
          </MappingItem>
        ))}
      </MappingContainer>
      
      {/* The button that shows the import dialog */}
      <ImportButton 
        onClick={() => {
          logger.log('🎯 Step 2 button clicked - showing import dialog directly');
          setShowingImportDialog(true);
        }} 
        disabled={isLoading || sheetHeaders.length === 0 || Object.values(getFilteredMappings()).length === 0}
      >
        {isLoading ? <FaSpinner className="spinner" /> : "Import Leads"}
      </ImportButton>
    </StepContainer>
  );


case 3:
  // If showing import dialog, show it
  if (showingImportDialog) {
    return (
      <ImportDialog
        onRegularImport={handleRegularImport}
        onSyncImport={handleSyncImport}
        onCancel={() => setShowingImportDialog(false)}
        isLoading={isLoading}
      />
    );
  }

  // Otherwise show success page (this is the new step 3)
  return (
    <StepContainer>
      <StepTitle>Import Successful</StepTitle>
      <CompletionContent>
        <FaCheckCircle className="success-icon" />
        <h3>Import Complete! 🎉</h3>
        <p style={{ marginBottom: '24px' }}>
          Your leads have been successfully imported from Google Sheets and are now available in your CRM.
        </p>
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
              {sheetHeaders.length}
            </div>
            <div style={{ color: '#666' }}>Fields Mapped</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
              ✓
            </div>
            <div style={{ color: '#666' }}>Successfully Imported</div>
          </div>
        </div>
        
        <CloseButton onClick={() => setCurrentStep(-1)}>
          Close & Finish
        </CloseButton>
      </CompletionContent>
    </StepContainer>
  );
  
      default:
        return null;
    }
  };

  // ENHANCED STEP INDICATORS - following CSV pattern
  const renderStepIndicators = () => {
    const steps = [
      { number: 1, title: "Connect", description: "Google authentication", icon: Upload },
      { number: 2, title: "Select", description: "Choose your sheet", icon: ArrowRightLeft },
      { number: 3, title: "Map", description: "Match columns", icon: ArrowRightLeft },

      { number: 5, title: "Complete", description: "Import finished", icon: Trophy }
    ];

    return (
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{
          margin: '0 0 24px 0',
          fontSize: '18px',
          fontWeight: '700',
          color: '#1e293b',
          textAlign: 'center'
        }}>
          Google Sheets Import
        </h3>
        
        {/* Progress line */}
        <div 
          style={{
            position: 'absolute',
            left: '50%',
            top: '120px',
            bottom: '120px',
            width: '3px',
            background: `repeating-linear-gradient(
              to bottom,
              #11998e 0px,
              #11998e 8px,
              transparent 8px,
              transparent 16px
            )`,
            transform: 'translateX(-50%)',
            zIndex: 1,
            opacity: 0.6
          }} 
        />

        {/* Steps */}
        {steps.map((step, index) => {
          const isActive = currentStep === index;
          const isCompleted = currentStep > index;
          const isClickable = currentStep > index;
          const StepIcon = step.icon;
          
          return (
            <div 
              key={step.number}
              style={{
                position: 'relative',
                zIndex: 2,
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                marginBottom: index < steps.length - 1 ? '24px' : '0'
              }}
              onClick={isClickable ? () => setCurrentStep(index) : undefined}
            >
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px',
                  background: isActive 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                    : isCompleted 
                    ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  borderRadius: '12px',
                  border: isActive 
                    ? '2px solid #667eea' 
                    : isCompleted 
                    ? '2px solid #11998e'
                    : '2px solid #e2e8f0',
                  boxShadow: isActive || isCompleted
                    ? '0 8px 25px rgba(0, 0, 0, 0.15)'
                    : '0 2px 8px rgba(0, 0, 0, 0.05)',
                  transform: isActive ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
                }}
              >
                {/* Icon Container */}
                <div 
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: isActive || isCompleted ? 'rgba(255, 255, 255, 0.2)' : '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    border: isActive || isCompleted ? '2px solid rgba(255, 255, 255, 0.3)' : '2px solid #e2e8f0',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {isCompleted ? (
                    <Check 
                      size={24} 
                      color="white"
                      strokeWidth={3}
                    />
                  ) : (
                    <StepIcon 
                      size={24} 
                      color={isActive ? 'white' : isCompleted ? 'white' : '#64748b'}
                      strokeWidth={2}
                    />
                  )}
                </div>

                {/* Step Content */}
                <div style={{ flex: 1 }}>
                  <div 
                    style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: isActive || isCompleted ? 'white' : '#334155',
                      marginBottom: '4px',
                      letterSpacing: '-0.025em'
                    }}
                  >
                    {step.title}
                  </div>
                  <div 
                    style={{
                      fontSize: '13px',
                      color: isActive || isCompleted ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
                      fontWeight: '400',
                      lineHeight: '1.4'
                    }}
                  >
                    {step.description}
                  </div>
                </div>

                {/* Step Number Badge */}
                <div 
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: isCompleted 
                      ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                      : isActive 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                  }}
                >
                  {isCompleted ? <Check size={12} strokeWidth={3} /> : step.number}
                </div>
              </div>
            </div>
          );
        })}

        {/* Progress Footer */}
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: 'rgba(255, 255, 255, 0.6)',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '12px',
            color: '#64748b',
            fontWeight: '500'
          }}>
            Step {currentStep + 1} of 5
          </div>

          <div style={{
            width: '100%',
            height: '4px',
            background: '#e2e8f0',
            borderRadius: '2px',
            marginTop: '8px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${((currentStep + 1) / 4) * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)',
              borderRadius: '2px',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      </div>
    );
  };



  return (
    <>
      {/* PRESERVED DROPDOWN TRIGGER - 100% EXACT */}
      <DropdownItem 
        onClick={() => {
          setCurrentStep(0);
          setIsLoading(true);
        }}
      >
        <FaGoogleDrive />
        Import from Google Sheets
      </DropdownItem>

      {/* ENHANCED POPUP - replacing DrawerComponent */}
      <PopupComponent
        isOpen={currentStep !== -1}
        onClose={() => setFormCloseModel(true)}
        title="Google Sheets Import"
        size="lg"
        loading={isLoading}
        headerActions={
          currentStep > 0 ? (
            <DisconnectButton
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              style={{
                position: 'static',
                margin: 0,
                padding: '6px 12px',
                fontSize: '13px',
                background: 'transparent',
                border: '1px solid #dc2626',
                borderRadius: '6px',
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {isDisconnecting ? (
                <FaSpinner className="spinner" style={{ fontSize: '12px' }} />
              ) : (
                <FaSignOutAlt style={{ fontSize: '12px' }} />
              )}
              Disconnect
            </DisconnectButton>
          ) : null
        }
      >
        <PopupContentContainer>
          {/* Left Sidebar - Step Navigation */}
          <LeftSidebar>
            {renderStepIndicators()}
          </LeftSidebar>

          {/* Right Content Area */}
          <RightContent>
            {/* Error Display */}
            {error && (
              <ErrorDisplay>
                <span style={{ color: '#ff4d4f', marginRight: '8px' }}>⚠️</span>
                <span style={{ color: '#ff4d4f' }}>{error}</span>
                <button 
                  onClick={() => setError(null)}
                  style={{ 
                    marginLeft: 'auto', 
                    background: 'none', 
                    border: 'none', 
                    color: '#ff4d4f',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
              </ErrorDisplay>
            )}
            
            {/* Main Content */}
            <ContentArea>
              {renderStep()}
            </ContentArea>
          </RightContent>
        </PopupContentContainer>
      </PopupComponent>

      {/* PRESERVED CONFIRMATION MODAL - 100% EXACT */}
   <GenericConfirmationModal
  onConfirmation={() => {
    setFormCloseModel(false);
    setCurrentStep(-1);
  }}
  showModal={formCloseModel}
  toggleModal={() => setFormCloseModel(false)}
  title="Confirm Close"
  message="Are you sure you want to close? All progress will be lost."
  isWarning={true}
  confirmText="Yes, Close"
  cancelText="Cancel"
  style={{ zIndex: 10000 }}
/>
    </>
  );
};

// PRESERVED EXPORT - 100% EXACT
export default UploadSheetFile;